import { PokemonCard } from '../types/pokemon';
import { OnePieceCard } from '../types/onepiece';

export interface PricingOptions {
  condition?: string;
  isFoil?: boolean;
}

/**
 * Normalizes a condition string to match TCGPlayer price keys
 */
export function normalizeCondition(condition?: string): string {
  if (!condition) return 'normal';
  
  const c = condition.toLowerCase().replace(/_/g, ' ');
  
  // TCGPlayer typically just stores prices under 'normal', 'holofoil', 'reverseHolofoil', or '1stEditionHolofoil'
  // But wait, the API returns prices as an object mapping printing -> price object (low, mid, high, market).
  // The condition is not part of the top level object in pokemon-tcg-data.
  // Example: 
  // card.tcgplayer.prices = {
  //   normal: { low: 1, mid: 2, high: 3, market: 1.5 },
  //   holofoil: { low: 2, mid: 4, high: 6, market: 3 }
  // }
  
  return c;
}

/**
 * Calculates the best market price from a raw card object based on conditions and printings
 */
export function calculateCardMarketPrice(
  card: any, 
  game: 'pokemon' | 'onepiece',
  options?: PricingOptions
): number {
  if (!card) return 0;

  if (game === 'pokemon') {
    const pCard = card as PokemonCard;
    if (!pCard.tcgplayer?.prices) return 0;
    
    // TCGPlayer prices for pokemon are separated by printing:
    // normal, holofoil, reverseHolofoil, 1stEditionHolofoil, etc.
    const prices = pCard.tcgplayer.prices as any;
    
    // Determine target printing
    let targetPrinting = 'normal';
    if (options?.isFoil) {
      if (prices.holofoil) targetPrinting = 'holofoil';
      else if (prices.reverseHolofoil) targetPrinting = 'reverseHolofoil';
    } else {
      if (prices.normal) targetPrinting = 'normal';
      else {
        // Fallback to the first available printing if requested printing is missing
        targetPrinting = Object.keys(prices)[0];
      }
    }

    const targetPrices = prices[targetPrinting];
    if (targetPrices) {
      return targetPrices.market || targetPrices.mid || targetPrices.low || 0;
    }

    // Ultimate fallback: grab the first available market price
    const fallbackPrices = Object.values(prices)[0] as any;
    if (fallbackPrices) {
      return fallbackPrices.market || fallbackPrices.mid || fallbackPrices.low || 0;
    }
  }

  if (game === 'onepiece') {
    const oCard = card as OnePieceCard;
    // One Piece JSON data typically just has a flat `prices` object: { low, mid, high, market }
    if (oCard.prices) {
      return oCard.prices.market || oCard.prices.mid || oCard.prices.low || 0;
    }
  }

  return 0;
}
